import { useMemo } from 'react';

import {
  AnalysisRemarksRestApiFactory,
  AnalysisRemarksSearchRestApiFactory,
  Configuration,
  ExperienceCompaniesRestApiFactory,
  InterviewSimulationsRestApiFactory,
  MeetingsRestApiFactory,
  ProcessCandidatesSearchRestApiFactory,
  ProcessesCandidatesRestApiFactory,
  ProcessesCvRestApiFactory,
  ProcessesRestApiFactory,
  ProcessesSearchRestApiFactory,
  ProcessesStarterRestApiFactory,
  ProcessesUpdaterRestApiFactory,
  ProcessTemplateRestApiFactory,
  StatisticsRestApiFactory,
  UsersRestApiFactory,
  UserToursRestApiFactory,
} from '@/api/generated';
import CONFIG from '@/config/config';

import { useAxiosInstance } from './useAxiosInstance.util';

interface Return {
  analysisRemarksApi: ReturnType<typeof AnalysisRemarksRestApiFactory>;
  analysisRemarksSearchApi: ReturnType<typeof AnalysisRemarksSearchRestApiFactory>;
  experienceCompaniesApi: ReturnType<typeof ExperienceCompaniesRestApiFactory>;
  interviewSimulationsApi: ReturnType<typeof InterviewSimulationsRestApiFactory>;
  meetingsApi: ReturnType<typeof MeetingsRestApiFactory>;
  processCandidatesApi: ReturnType<typeof ProcessesCandidatesRestApiFactory>;
  processCandidatesSearchApi: ReturnType<typeof ProcessCandidatesSearchRestApiFactory>;
  processesApi: ReturnType<typeof ProcessesRestApiFactory>;
  processesCvApi: ReturnType<typeof ProcessesCvRestApiFactory>;
  processesSearchApi: ReturnType<typeof ProcessesSearchRestApiFactory>;
  processesStarterApi: ReturnType<typeof ProcessesStarterRestApiFactory>;
  processesUpdaterApi: ReturnType<typeof ProcessesUpdaterRestApiFactory>;
  processTemplatesApi: ReturnType<typeof ProcessTemplateRestApiFactory>;
  statisticsApi: ReturnType<typeof StatisticsRestApiFactory>;
  usersApi: ReturnType<typeof UsersRestApiFactory>;
  userToursApi: ReturnType<typeof UserToursRestApiFactory>;
}

export const useApiClient = (): Return => {
  const basePath = CONFIG.HOST;
  const configuration = getConfiguration(basePath);
  const axiosInstance = useAxiosInstance();

  return useMemo(() => {
    return {
      analysisRemarksApi: AnalysisRemarksRestApiFactory(configuration, undefined, axiosInstance),
      analysisRemarksSearchApi: AnalysisRemarksSearchRestApiFactory(configuration, undefined, axiosInstance),
      experienceCompaniesApi: ExperienceCompaniesRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      interviewSimulationsApi: InterviewSimulationsRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      meetingsApi: MeetingsRestApiFactory(configuration, undefined, axiosInstance),
      processCandidatesApi: ProcessesCandidatesRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processCandidatesSearchApi: ProcessCandidatesSearchRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      processesApi: ProcessesRestApiFactory(configuration, undefined, axiosInstance),
      processesCvApi: ProcessesCvRestApiFactory(configuration, undefined, axiosInstance),
      processesSearchApi: ProcessesSearchRestApiFactory(configuration, undefined, axiosInstance),
      processesStarterApi: ProcessesStarterRestApiFactory(configuration, undefined, axiosInstance),
      processesUpdaterApi: ProcessesUpdaterRestApiFactory(configuration, undefined, axiosInstance),
      processTemplatesApi: ProcessTemplateRestApiFactory(configuration, undefined, axiosInstance),
      statisticsApi: StatisticsRestApiFactory(configuration, undefined, axiosInstance),
      usersApi: UsersRestApiFactory(configuration, undefined, axiosInstance),
      userToursApi: UserToursRestApiFactory(configuration, undefined, axiosInstance),
    };
  }, [axiosInstance, configuration]);
};

const getConfiguration = (basePath: string): Configuration => ({
  basePath,
  isJsonMime(_mime: string): boolean {
    return true;
  },
});
